"use client";

import React from "react";
import { SectionContentProps } from "../../../types";
import { FooterContent, SocialLink } from "../types";
import { SocialLinks, LinkGroupComponent, Copyright, getSocialIcon } from "../BaseFooter";
import { colors, footerStyles, layout, spacing } from "../tokens";

// ============================================================
// THREE COLUMN FOOTER - Balanced 3-column layout
// ============================================================
// Best for: SaaS, Technology companies, Service businesses
// Features: Balanced columns, clear navigation hierarchy
// ============================================================

interface ThreeColumnFooterProps extends SectionContentProps {
  content: FooterContent;
}

export const ThreeColumnFooter: React.FC<ThreeColumnFooterProps> = ({
  content: propContent,
  onContentChange,
  isSelected,
}) => {
  const content = propContent || {};
  const fontFamily = content.fontFamily || "'Inter', sans-serif";

  // Dynamic colors with fallbacks
  const backgroundColor = content.backgroundColor || "#0f172a"; // slate-900
  const textColor = content.textColor || "#ffffff";
  const accentColor = content.accentColor || "#3b82f6"; // blue-500

  const socialLinks: SocialLink[] = content.socialLinks || [
    { platform: "twitter", url: "#" },
    { platform: "linkedin", url: "#" },
    { platform: "github", url: "#" },
  ];

  const defaultLinkGroups = [
    {
      title: "Explore",
      links: [
        { label: "Home", href: "/" },
        { label: "Products", href: "/products" },
        { label: "Solutions", href: "/solutions" },
        { label: "Pricing", href: "/pricing" },
      ],
    },
    {
      title: "Company",
      links: [
        { label: "About", href: "/about" },
        { label: "Blog", href: "/blog" },
        { label: "Careers", href: "/careers" },
        { label: "Contact", href: "/contact" },
      ],
    },
    {
      title: "Connect",
      links: [
        { label: "Twitter", href: "#twitter" },
        { label: "LinkedIn", href: "#linkedin" },
        { label: "GitHub", href: "#github" },
      ],
    },
  ];

  const linkGroups = content.linkGroups || defaultLinkGroups;

  return (
    <footer
      className={`${isSelected ? "ring-2 ring-blue-500" : ""}`}
      style={{
        fontFamily,
        backgroundColor,
        color: textColor
      }}
      role="contentinfo"
      aria-label="Three column footer"
    >
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Top Section with Brand and 3 Columns */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 mb-16">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              {content.logo && (
                <img
                  src={content.logo}
                  alt="Logo"
                  className="h-8 w-auto brightness-0 invert"
                />
              )}
              <span className="text-xl font-bold tracking-wider">
                {content.companyName || "INSTACKS"}
              </span>
            </div>
            {content.description && (
              <p className="text-slate-400 text-sm leading-relaxed mb-6 max-w-sm">
                {content.description}
              </p>
            )}
            <SocialLinks links={socialLinks} size="md" />
          </div>

          {/* Link Columns */}
          {linkGroups.map((group, index) => (
            <div key={index}>
              <h4 className="text-white font-bold mb-4">{group.title}</h4>
              <ul className="space-y-3" role="list">
                {group.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    <a
                      href={link.href}
                      className="text-slate-400 text-sm hover:text-blue-400 transition-colors"
                      target={(link as any).isExternal ? "_blank" : undefined}
                      rel={(link as any).isExternal ? "noopener noreferrer" : undefined}
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Section */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 pt-8 border-t border-slate-800">
          <Copyright
            text={content.copyright || "© 2026 INSTACKS. All rights reserved."}
            className="text-slate-500"
          />

          {/* Legal Links */}
          <div className="flex items-center gap-6 text-sm">
            <a href="#" className="text-slate-400 hover:text-white transition-colors">
              Privacy
            </a>
            <a href="#" className="text-slate-400 hover:text-white transition-colors">
              Terms
            </a>
            <a href="#" className="text-slate-400 hover:text-white transition-colors">
              Cookies
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default ThreeColumnFooter;
